import { useCallback, useEffect, useRef, useState } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import { UserSimStreamRequest } from "api/userSimApi";
import { AutoFillValues } from "logic/autoFill";

export type AutoFillPhase = "idle" | "running" | "canceling" | "done" | "error";

export type AutoFillStepStatus = "running" | "done";

// 생각 요약 글 하나 (REASONING_MESSAGE_START … END). kind: REASONING_MESSAGE_START.metadata.kind — 없으면 예전 Agent
export type AutoFillReasoningKind = "plan" | "thought";
export type AutoFillReasoning = {
  id: string;
  text: string;
  kind?: AutoFillReasoningKind;
};

// 진행 단계 (AG-UI STEP_STARTED/FINISHED) 와 그 단계에서 쓴 툴 (TOOL_CALL_START/END)·생각 요약 (REASONING_MESSAGE_*)
export type AutoFillStep = {
  name: string;
  label: string;
  status: AutoFillStepStatus;
  startedAt: number;
  finishedAt?: number;
  // 단계 결과 요약 — STEP_FINISHED(없으면 STEP_STARTED)의 metadata.summary
  summary?: string;
  tools: { id: string; label: string; status: AutoFillStepStatus }[];
  // 이 단계의 생각 요약 글 — 받은 순서
  reasoning: AutoFillReasoning[];
};

// 칸 상태 — 값이 아직 없으면 없음(대기).
// writing: STATE_DELTA 로 온 초안(검증 전), rewritten: 수정 단계에서 같은 칸이 다시 옴, confirmed: STATE_SNAPSHOT 으로 확정
export type AutoFillFieldStatus = "writing" | "rewritten" | "confirmed";

// Agent 가 60초 동안 아무 이벤트도 보내지 않으면 실패로 본다 (계약 3장 keepalive)
export const AUTO_FILL_IDLE_TIMEOUT_MS = 60_000;

// 요청 직전 프론트가 하는 단계 (칸 설계도 만들기)
const PREPARE_STEP = "prepare";
// 검증에 걸린 칸을 다시 쓰는 단계 (계약 3.4)
const REVISE_STEP = "revise";

const labelOf = (
  event: { metadata?: Record<string, any> },
  fallback: string
) =>
  typeof event.metadata?.label === "string" ? event.metadata.label : fallback;

const summaryOf = (event: { metadata?: Record<string, any> }) =>
  typeof event.metadata?.summary === "string"
    ? event.metadata.summary
    : undefined;

// 수정 단계에서 다시 쓰는 칸과 이유 — STEP_STARTED(revise).metadata.fields
const reviseFieldsOf = (event: { metadata?: Record<string, any> }) => {
  const fields = event.metadata?.fields;
  if (!Array.isArray(fields)) return undefined;
  return fields
    .filter((field) => typeof field?.key === "string")
    .map((field) => ({
      key: field.key as string,
      reason: typeof field.reason === "string" ? field.reason : "",
    }));
};

// 요청을 만든 뒤 user_sim Agent 를 실행해(AG-UI) 진행 단계·지금 쓰는 칸·칸 값을 모은다.
export default function useAutoFillStream() {
  const [phase, setPhase] = useState<AutoFillPhase>("idle");
  const [request, setRequest] = useState<UserSimStreamRequest>();
  const [steps, setSteps] = useState<AutoFillStep[]>([]);
  // 지금 쓰고 있는 칸 (마지막 STATE_DELTA 의 칸)
  const [currentKey, setCurrentKey] = useState<string>();
  const [values, setValues] = useState<AutoFillValues>({});
  const [fieldStatus, setFieldStatus] = useState<
    Record<string, AutoFillFieldStatus>
  >({});
  // 다시 쓰는 이유 — 마지막 수정 단계의 metadata.fields (칸 key → 이유)
  const [rewriteReasons, setRewriteReasons] = useState<Record<string, string>>(
    {}
  );
  const [error, setError] = useState<string>();

  const agentRef = useRef<AbstractAgent>();
  const canceledRef = useRef(false);
  const runRef = useRef<Promise<void>>();

  const start = useCallback(
    (getRequest: () => Promise<UserSimStreamRequest>) => {
      canceledRef.current = false;
      agentRef.current = undefined;
      setPhase("running");
      setRequest(undefined);
      setSteps([
        {
          name: PREPARE_STEP,
          label: "활동지 정리",
          status: "running",
          startedAt: Date.now(),
          tools: [],
          reasoning: [],
        },
      ]);
      setCurrentKey(undefined);
      setValues({});
      setFieldStatus({});
      setRewriteReasons({});
      setError(undefined);

      const updateStep = (
        name: string,
        update: (step: AutoFillStep) => AutoFillStep
      ) =>
        setSteps((prev) => {
          // 같은 이름이 여러 번 열리면 마지막 것
          const index = prev.map((step) => step.name).lastIndexOf(name);
          return index < 0
            ? prev
            : prev.map((step, i) => (i === index ? update(step) : step));
        });

      // 진행 중인 마지막 단계
      const updateRunningStep = (
        update: (step: AutoFillStep) => AutoFillStep
      ) =>
        setSteps((prev) => {
          const index = prev.map((step) => step.status).lastIndexOf("running");
          return index < 0
            ? prev
            : prev.map((step, i) => (i === index ? update(step) : step));
        });

      runRef.current = (async () => {
        let finished = false;
        let failure: string | undefined;
        let idleTimer: ReturnType<typeof setTimeout> | undefined;
        // 마지막으로 열린 단계 (다시 쓴 칸 판별)
        let stepName: string | undefined;
        // 수정 단계가 다시 쓸 칸을 알려 줬는지 — 알려 줬으면 그 칸만 다시 쓴 칸
        let hasReviseFields = false;
        try {
          const req = await getRequest();
          if (canceledRef.current) return;
          setRequest(req);
          updateStep(PREPARE_STEP, (step) => ({
            ...step,
            status: "done",
            finishedAt: Date.now(),
          }));

          const { createUserSimAgent, fromValuePath } = await import(
            "api/userSimAgent"
          );
          if (canceledRef.current) return;
          const agent = createUserSimAgent(req);
          agentRef.current = agent;

          const armIdle = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
              failure = "Agent 응답이 없어요. 다시 시도해 주세요";
              agent.abortRun();
            }, AUTO_FILL_IDLE_TIMEOUT_MS);
          };
          armIdle();

          await agent
            .runAgent(
              {},
              {
                onEvent: armIdle,
                onStepStartedEvent: ({ event }) => {
                  stepName = event.stepName;
                  const reviseFields =
                    event.stepName === REVISE_STEP
                      ? reviseFieldsOf(event)
                      : undefined;
                  hasReviseFields = !!reviseFields;
                  // 재시도마다 그때 다시 쓰는 칸
                  if (reviseFields) {
                    setRewriteReasons(
                      Object.fromEntries(
                        reviseFields.map(({ key, reason }) => [key, reason])
                      )
                    );
                    setFieldStatus((prev) => ({
                      ...prev,
                      ...Object.fromEntries(
                        reviseFields.map(({ key }) => [
                          key,
                          "rewritten" as const,
                        ])
                      ),
                    }));
                  }
                  setSteps((prev) => [
                    ...prev,
                    {
                      name: event.stepName,
                      label: labelOf(event, event.stepName),
                      status: "running",
                      startedAt: Date.now(),
                      summary: summaryOf(event),
                      tools: [],
                      reasoning: [],
                    },
                  ]);
                },
                onStepFinishedEvent: ({ event }) =>
                  updateStep(event.stepName, (step) => ({
                    ...step,
                    status: "done",
                    finishedAt: Date.now(),
                    summary: summaryOf(event) ?? step.summary,
                  })),
                onToolCallStartEvent: ({ event }) =>
                  updateRunningStep((step) => ({
                    ...step,
                    tools: [
                      ...step.tools,
                      {
                        id: event.toolCallId,
                        label: labelOf(event, event.toolCallName),
                        status: "running",
                      },
                    ],
                  })),
                onToolCallEndEvent: ({ event }) =>
                  setSteps((prev) =>
                    prev.map((step) => ({
                      ...step,
                      tools: step.tools.map((tool) =>
                        tool.id === event.toolCallId
                          ? { ...tool, status: "done" }
                          : tool
                      ),
                    }))
                  ),
                // 생각 요약 — 글(messageId)마다 delta 를 이어 붙인다
                onReasoningMessageStartEvent: ({ event }) =>
                  updateRunningStep((step) => ({
                    ...step,
                    reasoning: [
                      ...step.reasoning,
                      {
                        id: event.messageId,
                        text: "",
                        kind:
                          event.metadata?.kind === "plan" ||
                          event.metadata?.kind === "thought"
                            ? event.metadata.kind
                            : undefined,
                      },
                    ],
                  })),
                onReasoningMessageContentEvent: ({ event }) =>
                  updateRunningStep((step) => {
                    const index = step.reasoning.findIndex(
                      (message) => message.id === event.messageId
                    );
                    return {
                      ...step,
                      reasoning:
                        index < 0
                          ? [
                              ...step.reasoning,
                              { id: event.messageId, text: event.delta },
                            ]
                          : step.reasoning.map((message, i) =>
                              i === index
                                ? {
                                    ...message,
                                    text: message.text + event.delta,
                                  }
                                : message
                            ),
                    };
                  }),
                onStateDeltaEvent: ({ event }) => {
                  const keys = event.delta
                    .map((op) => fromValuePath(op.path))
                    .filter((key): key is string => !!key);
                  if (!keys.length) return;
                  setCurrentKey(keys[keys.length - 1]);
                  // 수정 단계에서 이미 받은 칸이 다시 오면 다시 쓴 칸 (다시 쓸 칸을 알려 줬으면 단계 시작 때 정함)
                  const isRevise = stepName === REVISE_STEP && !hasReviseFields;
                  setFieldStatus((prev) => {
                    const next = { ...prev };
                    keys.forEach((key) => {
                      next[key] =
                        prev[key] && isRevise
                          ? "rewritten"
                          : (prev[key] ?? "writing");
                    });
                    return next;
                  });
                },
                // 스냅샷의 값이 최종 값 — 모두 확정
                onStateSnapshotEvent: ({ event }) =>
                  setFieldStatus(
                    Object.fromEntries(
                      Object.keys(event.snapshot?.values || {}).map((key) => [
                        key,
                        "confirmed" as const,
                      ])
                    )
                  ),
                onStateChanged: ({ state }) =>
                  setValues({ ...(state?.values || {}) }),
                onRunFinishedEvent: () => {
                  finished = true;
                },
                // RUN_ERROR 를 받아도 runAgent 는 정상 종료한다 — 직접 기록
                onRunErrorEvent: ({ event }) => {
                  failure ??= event.message;
                },
                onRunFailed: ({ error }) => {
                  failure ??= error.message;
                },
              }
            )
            .catch((e: unknown) => {
              failure ??= e instanceof Error ? e.message : String(e);
            });

          if (canceledRef.current) return;
          // RUN_FINISHED 없이 끝나도 runAgent 는 정상 종료한다
          if (!failure && !finished) failure = "응답이 중간에 끊겼어요";
          if (failure) throw new Error(failure);
          setPhase("done");
        } catch (e) {
          if (canceledRef.current) return;
          setError(e instanceof Error ? e.message : String(e));
          setPhase("error");
        } finally {
          clearTimeout(idleTimer);
        }
      })();
    },
    []
  );

  // 요청·스트림을 완전히 멈춘 뒤 닫는다
  const cancel = useCallback(async () => {
    setPhase("canceling");
    canceledRef.current = true;
    agentRef.current?.abortRun();
    await runRef.current;
    setPhase("idle");
  }, []);

  const reset = useCallback(() => setPhase("idle"), []);

  useEffect(
    () => () => {
      canceledRef.current = true;
      agentRef.current?.abortRun();
    },
    []
  );

  return {
    phase,
    request,
    steps,
    currentKey,
    values,
    fieldStatus,
    rewriteReasons,
    error,
    start,
    cancel,
    reset,
  };
}
